import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light.css'

import BusinessCentreIcon from '@assets/icons/businessCentre.svg'
import DownChevronIcon from '@assets/icons/chevronDown.svg'
import RightChevronIcon from '@assets/icons/chevronRight.svg'
import UpChevronIcon from '@assets/icons/chevronUp.svg'
import GRLogoIcon from '@assets/icons/GRLogo.svg'
import LCVIcon from '@assets/icons/LCV.svg'
import LexusShowroomIcon from '@assets/icons/lexusShowRoom.svg'
import UsedCarIcon from '@assets/icons/lexusUsedCars.svg'
import PartsIcon from '@assets/icons/parts.svg'
import PhoneIcon from '@assets/icons/phone.svg'
import PinIcon from '@assets/icons/pin.svg'
import ServicingIcon from '@assets/icons/servicing.svg'
import ToyotaInfoIcon from '@assets/icons/toyotaInfo.svg'
import ToyotaShowroomIcon from '@assets/icons/toyotaShowroom.svg'
import WebsiteIcon from '@assets/icons/website.svg'
import { CTA } from '@components/ui'
import { useBrand } from '@contexts/useBrand'
import Tippy from '@tippyjs/react'
import React, { useEffect, useRef, useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { upperCaseFirst } from '@utils/helpers/stringHelpers'

const OpeningTimes = ({ openingDays, serviceType }) => {
  const brand = useBrand()
  const filteredOpeningDays = openingDays.filter(
    (day) => day.service === serviceType,
  )

  return (
    <div className="max-w-[90%]">
      <h3
        className={`${
          brand === 'lexus' ? 'test-tiny' : 'text-base'
        } font-medium mb-2`}
      >
        {serviceType}
      </h3>
      <ul>
        {filteredOpeningDays.map((day, index) => (
          <li key={index} className="mb-4">
            <p>
              {day.startDay}
              {day.startDay !== day.endDay ? ` - ${day.endDay}` : ''}
            </p>
            <p>
              {day.hours[0].startTime}-{day.hours[0].endTime}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Card = ({
  dealer,
  setDealer,
  cardNumber,
  isSelected,
  onSelectDealerID,
  scrollRef,
}) => {
  const brand = useBrand()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [userLocation, setUserLocation] = useState({
    userLatitude: '',
    userLongitude: '',
  })
  const cardResultRef = useRef(null)

  const toggleExpanded = () => {
    setIsExpanded((prevExpanded) => !prevExpanded)
  }

  useEffect(() => {
    function closeOnScroll() {
      setIsModalOpen(false)
    }

    scrollRef?.current?.addEventListener('scroll', closeOnScroll, false)

    return () => {
      scrollRef?.current?.removeEventListener('scroll', closeOnScroll, false)
    }
  }, [])

  useEffect(() => {
    if (isSelected) {
      cardResultRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isSelected])

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((permissionStatus) => {
          if (permissionStatus.state === 'granted') {
            navigator.geolocation.getCurrentPosition((position) => {
              const { latitude, longitude } = position.coords
              setUserLocation({
                userLatitude: latitude,
                userLongitude: longitude,
              })
            })
          }
        })
    }
  }, [])

  const dealerObject = {
    id: dealer.localDealerID,
    name: dealer.name,
    address: dealer.address,
    phone: dealer.phone,
    distance: dealer.distance,
  }

  const handleCardClick = () => {
    onSelectDealerID(dealer.localDealerID)
    window.customMap.focusOn({ coordinates: dealer.address.geo })
  }

  if (!dealer) {
    return null
  }

  const { address } = dealer
  const directionsLink = `https://www.google.com/maps/dir/${
    userLocation.userLatitude && userLocation.userLongitude
      ? `${userLocation.userLatitude},${userLocation.userLongitude}`
      : ''
  }/${address.geo.lat},${address.geo.lon}`

  const handleDistance = (distance) => {
    const miles = distance * 0.621371
    return (
      <NumericFormat
        value={miles}
        displayType={'text'}
        decimalScale={1}
        suffix={' MILES'}
        thousandSeparator={true}
      />
    )
  }

  const showRoomIcon =
    brand === 'toyota' ? <ToyotaShowroomIcon /> : <LexusShowroomIcon />

  const serviceMapping = {
    icon: {
      WorkShop: <ServicingIcon />,
      ShowRoom: showRoomIcon,
      LCVCentre: <LCVIcon />,
      BusinessCenter: <BusinessCentreIcon />,
      ToyotaGazooRacing: <GRLogoIcon />,
      PartsShop: <PartsIcon />,
      UsedCars: <UsedCarIcon />,
    },
    tooltip: {
      WorkShop: `Can carry out Service, MOT and Maintenance on your ${upperCaseFirst(
        brand,
      )} through our fully trained, expert technicians.`,
      ShowRoom: `${
        brand === 'lexus'
          ? 'Sells new Lexus Vehicles.'
          : 'Sells new Toyota and Approved Used Vehicles.'
      }`,
      LCVCentre: 'Light Commercial Vehicle Specialist.',
      BusinessCenter:
        'Ready to support you and your business in choosing the right vehicles for your fleet.',
      ToyotaGazooRacing:
        'Has expert knowledge of the Toyota Gazoo Racing product range.',
      PartsShop: `Sells and fits parts for your ${upperCaseFirst(brand)}`,
      UsedCars: `Sells used ${upperCaseFirst(brand)} vehicles`,
    },
  }

  return (
    <div
      ref={cardResultRef}
      id="card-result"
      data-card-index={cardNumber}
      data-dealer-id={dealer.localDealerID}
      className="m-2 mb-8 flex flex-col justify-start shadow-lg text-tiny cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="h-full flex flex-col m-6">
        <div className="flex flex-row items-center justify-between">
          <h5 className="text-lg mt-4 font-medium">{dealer.name}</h5>
          <div
            className={`bg-${isSelected ? 'base' : 'white'} text-${
              isSelected ? 'white' : 'base'
            } rounded-full w-8 h-8 p-2 flex items-center justify-center z-10 ${
              isSelected ? '' : 'border-2 border-base'
            }`}
          >
            {cardNumber}
          </div>
        </div>
        <hr style={{ border: '1px solid #ddd' }} className="my-6" />
        <p>{address.address1}</p>
        <p>{address.city}</p>
        <p>{address.region}</p>
        <p>{address.zip}</p>
        <br />
        <p className="font-semibold flex flex-row align-baseline">
          <PinIcon className="mr-1" />
          {handleDistance(dealer.distance)}
        </p>
        <br />
        <div
          className={`overflow-hidden transition-max-height duration-300 ease-in-out ${
            isExpanded ? 'max-h-screen' : 'max-h-0'
          }`}
        >
          <hr style={{ border: '1px solid #ddd' }} className="my-6" />
          <h5 className="text-lg my-2 font-medium">Contact</h5>
          <p>
            Phone:{' '}
            <a
              href={`tel:${dealer.phone}`}
              data-gt-name="componentevent"
              data-gt-componentgroup="forms"
              data-gt-action="click-type"
              data-gt-label={dealer.phone}
              className="text-blue"
            >
              {dealer.phone}
            </a>
          </p>
          <p className="truncate">
            Website:{' '}
            <a
              className="truncate text-blue"
              href={dealer.url}
              data-gt-name="componentevent"
              data-gt-componentgroup="forms"
              data-gt-action="click-type"
              data-gt-label={dealer.url}
            >
              {dealer.url}
            </a>
          </p>
          <ul className="my-6">
            {dealer.services.map((service) => (
              <li key={service.service}>
                <div className="relative">
                  <div className="flex flex-row items-center">
                    <span className="mx-1">
                      {serviceMapping.icon[service.service]}
                    </span>{' '}
                    <span className="mx-1">{service.label}</span>
                    <Tippy
                      content={
                        <div>{serviceMapping.tooltip[service.service]}</div>
                      }
                      placement="top"
                      arrow
                      theme="black-tooltip"
                      className="p-2 rounded-none border border-white"
                    >
                      <div className="mx-1">
                        <ToyotaInfoIcon
                          data-gt-name="componentevent"
                          data-gt-componentgroup="forms"
                          data-gt-action="click-type"
                          data-gt-label="info"
                        />
                      </div>
                    </Tippy>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {brand === 'lexus' ? (
            <div className="grid grid-cols-2">
              <div>
                <OpeningTimes
                  openingDays={dealer.openingDays}
                  serviceType="Showroom"
                />
              </div>
              <div>
                <OpeningTimes
                  openingDays={dealer.openingDays}
                  serviceType="Workshop"
                />
              </div>
            </div>
          ) : (
            <div className="grid">
              <div>
                <OpeningTimes
                  openingDays={dealer.openingDays}
                  serviceType="Approved After Sales"
                />
              </div>
              <div>
                <OpeningTimes
                  openingDays={dealer.openingDays}
                  serviceType="Approved Sales"
                />
              </div>
            </div>
          )}
          <CTA
            className="w-[90%] m-4"
            href={directionsLink}
            target="_blank"
            data-gt-name="componentevent"
            data-gt-action="dealer-list-click-cta"
            data-gt-label="directions"
            style={{
              backgroundColor: 'white',
              border: '1px solid black',
              color: 'black',
            }}
          >
            <span className="mr-2 pointer-events-none">Get Directions</span>
          </CTA>
          <hr style={{ border: '1px solid #ddd' }} />
        </div>

        <div className="lg:hidden">
          <div
            className={`grid md:grid-cols-2 gap-4 overflow-hidden transition-max-height duration-300 ease-in-out max-h-${
              !isExpanded ? 'full' : '0'
            }`}
          >
            <div>
              <div className="flex flex-row">
                <PhoneIcon className="mr-1" />
                <p>
                  Call:{' '}
                  <a
                    href={`tel:${dealer.phone}`}
                    data-gt-name="componentevent"
                    data-gt-componentgroup="forms"
                    data-gt-action="click-type"
                    data-gt-label={dealer.phone}
                    className="text-blue"
                  >
                    {dealer.phone}
                  </a>
                </p>
              </div>
              <div className="flex flex-row items-center">
                <WebsiteIcon className="mr-1" />
                <a
                  className="truncate text-blue"
                  href={dealer.url}
                  rel="noreferrer"
                  data-gt-name="componentevent"
                  data-gt-componentgroup="forms"
                  data-gt-action="click-type"
                  data-gt-label={dealer.url}
                  style={{
                    display: 'inline-block',
                    maxWidth: 'calc(100% - 30px)',
                  }}
                >
                  <p>{`${dealer.url}`}</p>
                </a>
              </div>
              <a
                className="flex flex-row text-blue"
                data-gt-name="componentevent"
                data-gt-componentgroup="forms"
                data-gt-action="click-type"
                data-gt-label="directions"
                href={directionsLink}
                target="_blank"
                rel="noreferrer"
              >
                <PinIcon className="mr-1" />
                Get Directions
              </a>
            </div>
            <div>
              <p className="font-medium mb-1">Services</p>
              <ul className="flex flex-row">
                {dealer.services.map((service) => (
                  <li key={service.service}>
                    <Tippy
                      content={service.label}
                      placement="top"
                      arrow
                      theme="black-tooltip"
                      className="p-2 rounded-none border border-white"
                    >
                      <div
                        className="service-icon mx-[2px] focus:outline-none"
                        title={service.label}
                      >
                        {serviceMapping.icon[service.service]}
                      </div>
                    </Tippy>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="lg:hidden">
          <CTA
            className="w-[90%] m-4"
            onClick={() => setDealer(dealerObject)}
            data-gt-name="componentevent"
            data-gt-action="dealer-list-click-cta"
            data-gt-label="Select"
            style={{
              backgroundColor: isSelected ? 'black' : 'white',
              border: isSelected ? 'none' : '1px solid black',
              color: isSelected ? 'white' : 'black',
            }}
          >
            <span className="mr-2 pointer-events-none">Select</span>
          </CTA>
          <button className="w-[90%] m-4 md:" onClick={toggleExpanded}>
            {isExpanded ? (
              <span className="flex flex-row justify-center items-center">
                <UpChevronIcon className="mt-1 ml-1" />
              </span>
            ) : (
              <span className="flex flex-row justify-center items-center">
                View more <DownChevronIcon className="mt-1 ml-1" />
              </span>
            )}
          </button>
        </div>

        <div className="hidden lg:block">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex flex-row">
                <PhoneIcon className="mr-1" />
                <p>
                  Call:{' '}
                  <a
                    href={`tel:${dealer.phone}`}
                    data-gt-name="componentevent"
                    data-gt-componentgroup="forms"
                    data-gt-action="click-type"
                    data-gt-label={dealer.phone}
                    className="text-blue"
                  >
                    {dealer.phone}
                  </a>
                </p>
              </div>
              <div className="flex flex-row items-center">
                <WebsiteIcon className="mr-1" />
                <a
                  className="truncate text-blue"
                  href={dealer.url}
                  rel="noreferrer"
                  data-gt-name="componentevent"
                  data-gt-componentgroup="forms"
                  data-gt-action="click-type"
                  data-gt-label={dealer.url}
                  style={{
                    display: 'inline-block',
                    maxWidth: 'calc(100% - 30px)',
                  }}
                >
                  <p>{`${dealer.url}`}</p>
                </a>
              </div>
              <a
                className="flex flex-row text-blue"
                data-gt-name="componentevent"
                data-gt-componentgroup="forms"
                data-gt-action="click-type"
                data-gt-label="directions"
                href={directionsLink}
                target="_blank"
                rel="noreferrer"
              >
                <PinIcon className="mr-1" />
                Get Directions
              </a>
            </div>
            <div>
              <p className="font-medium mb-1">Services</p>
              <ul className="flex flex-row">
                {dealer.services.map((service) => (
                  <li key={service.service}>
                    <Tippy
                      content={service.label}
                      placement="top"
                      arrow
                      theme="black-tooltip"
                      className="p-2 rounded-none border border-white"
                    >
                      <div
                        className="service-icon mx-[2px] focus:outline-none"
                        title={service.label}
                      >
                        {serviceMapping.icon[service.service]}
                      </div>
                    </Tippy>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-row w-full">
              <CTA
                className="w-full !mx-4 !my-2 col-span-2 "
                onClick={() => setDealer(dealerObject)}
                data-gt-name="componentevent"
                data-gt-action="dealer-list-click-cta"
                data-gt-label="Select"
                style={{
                  backgroundColor: isSelected ? 'black' : 'white',
                  border: isSelected ? 'none' : '1px solid black',
                  color: isSelected ? 'white' : 'black',
                }}
              >
                <span className="mr-2 pointer-events-none">Select</span>
              </CTA>
            </div>
            <div className="lg:flex flex-row justify-end pl-4 w-full">
              <Tippy
                content={
                  <div className="m-4 z-100 text-sm">
                    <h5 className="text-lg my-2 font-medium">Contact</h5>
                    <p>
                      Phone:{' '}
                      <a
                        href={`tel:${dealer.phone}`}
                        data-gt-name="componentevent"
                        data-gt-componentgroup="forms"
                        data-gt-action="click-type"
                        data-gt-label={dealer.phone}
                        className="text-blue"
                      >
                        {dealer.phone}
                      </a>
                    </p>
                    <p className="truncate">
                      Website:{' '}
                      <a
                        className="truncate text-blue"
                        href={dealer.url}
                        data-gt-name="componentevent"
                        data-gt-componentgroup="forms"
                        data-gt-action="click-type"
                        data-gt-label={dealer.url}
                      >
                        {dealer.url}
                      </a>
                    </p>
                    <ul className="my-6">
                      {dealer.services.map((service) => (
                        <li key={service.service}>
                          <div className="relative">
                            <div className="flex flex-row items-center">
                              <span className="mx-1">
                                {serviceMapping.icon[service.service]}
                              </span>{' '}
                              <span className="mx-1">{service.label}</span>
                              <Tippy
                                content={
                                  <div>
                                    {serviceMapping.tooltip[service.service]}
                                  </div>
                                }
                                placement="top"
                                arrow
                                theme="black-tooltip"
                                className="p-2 rounded-none border border-white"
                              >
                                <div className="mx-1">
                                  <ToyotaInfoIcon
                                    data-gt-name="componentevent"
                                    data-gt-componentgroup="forms"
                                    data-gt-action="click-type"
                                    data-gt-label="info"
                                  />
                                </div>
                              </Tippy>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {brand === 'lexus' ? (
                      <div className="grid grid-cols-2">
                        <div>
                          <OpeningTimes
                            openingDays={dealer.openingDays}
                            serviceType="Showroom"
                          />
                        </div>
                        <div>
                          <OpeningTimes
                            openingDays={dealer.openingDays}
                            serviceType="Workshop"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2">
                        <div>
                          <OpeningTimes
                            openingDays={dealer.openingDays}
                            serviceType="Approved After Sales"
                          />
                        </div>
                        <div>
                          <OpeningTimes
                            openingDays={dealer.openingDays}
                            serviceType="Approved Sales"
                          />
                        </div>
                      </div>
                    )}
                    <CTA
                      className="w-[90%] m-4"
                      href={directionsLink}
                      rel="noreferrer"
                      data-gt-name="componentevent"
                      data-gt-action="dealer-list-click-cta"
                      data-gt-label="directions"
                      target="_blank"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid black',
                        color: 'black',
                      }}
                    >
                      <span className="mr-2 pointer-events-none">
                        Get Directions
                      </span>
                    </CTA>
                  </div>
                }
                interactive
                placement="right-end"
                arrow
                theme="light"
                onShow={() => setIsModalOpen(true)}
                onHide={() => setIsModalOpen(false)}
                maxWidth={350}
                visible={isModalOpen}
                onClickOutside={() => setIsModalOpen(false)}
              >
                <button
                  className="justify-end"
                  onClick={() => setIsModalOpen(!isModalOpen)}
                >
                  <span className="flex flex-row items-center">
                    {isModalOpen ? 'View less' : 'View more'}
                    <RightChevronIcon className="mt-1 ml-1" />
                  </span>
                </button>
              </Tippy>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Card
